import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { AdminCarouselItem, CarouselResult, CarouselType } from '@models/section/cardCarouselSection';
import { AdminApiService } from '@services/adminApiService';
import { LanguageCode } from '@models/languageCode';
import { getCarouselByTag } from '@services/tagPublicService';
import { getCarouselByCategory } from '@services/categoryPublicService';
import { PagedResult } from '@models/pagination';
import { TaggedSlugEntityPreview } from '@models/tag';
import { PublicApiService } from '@services/publicApiService';

export const defaultCarouselKey = 'carouselItems';

export function useCarouselItems(type: CarouselType, languageCode: LanguageCode): UseQueryResult<AdminCarouselItem[]> {
    const carouselQueryMapping: Record<CarouselType, ((value: string) => Promise<AdminCarouselItem[]>) | null> = {
        [CarouselType.Tag]: AdminApiService.tagAdminService.getCarouselItems,
        [CarouselType.Category]: AdminApiService.categoryAdminService.getCarouselItems,
        [CarouselType.Article]: null,
    };

    const carouselQueryFn = carouselQueryMapping[type];

    return useQuery({
        queryKey: [defaultCarouselKey, type, languageCode],
        queryFn: carouselQueryFn ? () => carouselQueryFn(languageCode) : undefined,
        enabled: !!carouselQueryFn,
    });
}

export function useTagCarousel(
    type: CarouselType,
    tagId?: number,
    taggedEntityType?: string
): UseQueryResult<CarouselResult> {
    return useQuery({
        queryKey: ['tag-carousel', tagId, taggedEntityType],
        queryFn: tagId ? () => getCarouselByTag(tagId, taggedEntityType) : undefined,
        enabled: type === CarouselType.Tag && typeof tagId === 'number',
    });
}

export function useCategoryCarousel(type: CarouselType, categoryId?: number): UseQueryResult<CarouselResult> {
    return useQuery({
        queryKey: ['category-carousel', categoryId],
        queryFn: categoryId ? () => getCarouselByCategory(categoryId) : undefined,
        enabled: type === CarouselType.Category && typeof categoryId === 'number',
    });
}

export function useArticleCarousel(
    type: CarouselType,
    languageId: number
): UseQueryResult<PagedResult<TaggedSlugEntityPreview>> {
    return useQuery({
        queryKey: ['article-carousel', languageId],
        queryFn: () => PublicApiService.pressReleasePublicService.getPagedPressReleaseList(1, 6, languageId),
        enabled: type === CarouselType.Article,
    });
}
